import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';

import { useMonetaryAmount } from '@sorare/core/src/hooks/useMonetaryAmount';
import { useEvents } from '@sorare/core/src/lib/events/useEvents';
import type {
  ClickAddToCart_Origin,
  ClickRemoveFromCart_Origin,
  ClickSaveForLater_Origin,
} from '@sorare/core/src/protos/events/so5/shared/events';

import { useCart } from '#src/hooks/useCart.ts';

import type { useCartEvents_anyCard } from './__generated__/useCartEvents.graphql.ts';

type Props = {
  anyCard: Nullable<useCartEvents_anyCard>;
  composeSessionId?: string;
};

export const useCartEvents = ({ anyCard, composeSessionId = '' }: Props) => {
  const { toMonetaryAmount } = useMonetaryAmount();

  const { cart } = useCart();
  const track = useEvents();

  const price = toMonetaryAmount(anyCard?.livePrimaryOffer?.price);

  const properties = {
    cartId: cart?.id || '',
    offerId: anyCard?.livePrimaryOffer?.id || '',
    cardSlug: anyCard?.slug || '',
    ethAmount: price?.eth,
    eurAmount: price?.eur,
    scarcity: anyCard?.rarityTyped || '',
    composeSessionId,
  };
  const trackClickAddToCart = (origin: ClickAddToCart_Origin) => {
    track('Click Add To Cart', {
      ...properties,
      origin,
    });
  };

  const trackClickSaveForLater = (origin: ClickSaveForLater_Origin) => {
    track('Click Save For Later', {
      ...properties,
      origin,
    });
  };

  const trackClickRemoveFromCart = (origin: ClickRemoveFromCart_Origin) => {
    track('Click Remove From Cart', {
      ...properties,
      origin,
    });
  };

  return {
    trackClickAddToCart,
    trackClickSaveForLater,
    trackClickRemoveFromCart,
  };
};

useCartEvents.fragments = {
  anyCard: gql`
    fragment useCartEvents_anyCard on AnyCardInterface {
      slug
      sport
      rarityTyped
      livePrimaryOffer {
        id
        price {
          ...useMonetaryAmount_monetaryAmount
        }
      }
    }
    ${useMonetaryAmount.fragments.monetaryAmount}
  ` as TypedDocumentNode<useCartEvents_anyCard>,
};
